import type {
  AdminPlanDto,
  BrandTemplateKind,
  BrandWorkspaceTemplate,
  ColorEmphasis,
  ColorMode,
  GenerateDesignRequest,
  GenerateDesignResponse,
  DesignPngCopy,
  PromptFromPostRequest,
  PromptFromPostResponse,
  DesignJobState,
  TemplateRegion,
  TemplateRoleMap,
  BillingAdminAccessDto,
  InstallIdentityDto,
  BillingScheduleDto,
  OneTimeFeeDto,
  BillingSummaryDto,
  CheckoutSessionResultDto,
  InvoiceDto,
  PlanDto,
  PlanSyncResultDto,
  PortalSessionResultDto,
  SeatMemberDto,
  SeatPolicyRequestDto,
  SeatUsageDto,
  TenantAccessDto,
  ApiError,
  ApprovalDecisionDto,
  BlogExportFormat,
  MarkPublishedDto,
  ContentChannel,
  ApprovalTransition,
  ApproverAssignment,
  Asset,
  AssetFolder,
  AssetPage,
  AuthPrincipal,
  BrandAssets,
  BrandCompanyVoiceSource,
  BrandCopySource,
  BrandGuidelinesExtraction,
  BrandImportChangesView,
  BrandImportJobView,
  BrandImportLatestResponse,
  BrandInformationSaveRequest,
  BrandOnboardingView,
  BrandProfileView,
  BrandSuggestionRequest,
  BrandSuggestionsResponse,
  BrandVoiceProfile,
  BrandWizardStep,
  BrandWorkspaceFlagView,
  OnboardingPositionRequest,
  OnboardingStepSaveRequest,
  StartBrandImportResponse,
  StartBrandRereadRequest,
  BulkAssetResult,
  CanvaImportDto,
  DesignBundlePreview,
  DesignToolItem,
  DesignToolsView,
  MediaGenerationUsage,
  MediaGenerationView,
  SaveGenerationDto,
  StartGenerationDto,
  FigmaFileView,
  FigmaImportDto,
  DesignBundleTarget,
  EnqueuePostDto,
  PostingSchedule,
  ScheduleSuggestionsResponse,
  UpdatePostingScheduleDto,
  SchedulingCategory,
  CreateSchedulingCategoryDto,
  UpdateSchedulingCategoryDto,
  SchedulingCategoryUsage,
  CalendarFilter,
  CalendarItem,
  Campaign,
  Content,
  CreateAssetFolderDto,
  CreateCampaignDto,
  CreateContentDto,
  CreatePostDto,
  DuplicatePostDto,
  MovePostDto,
  ReplaceContentsDto,
  ResolveThreadPostDto,
  CreateSourceDto,
  CreateTenantDto,
  CreateTrendTopicDto,
  DailyBrief,
  EntraDirectoryGroup,
  EntraDirectoryInfo,
  EntraGroupMember,
  FacebookPage,
  FacebookStartResult,
  FigmaIdentity,
  LinkedInApiType,
  LinkedInStartDto,
  LinkedInStartResult,
  LinkedInOrganization,
  InstagramAccount,
  InviteExternalApproverDto,
  InviteExternalApproverResult,
  InviteFromGroupDto,
  ManageableWorkspace,
  ManagedMember,
  MediaCredential,
  MediaQuery,
  OAuthRedirectUris,
  OAuthStartResult,
  ApifyIdentity,
  PlatformSettings,
  TrashActionDto,
  TrashItem,
  SiteAuditEntry,
  SiteIntegrationsView,
  CustomerDirectoryView,
  UpdateCustomerDirectoryDto,
  AiModelsView,
  AiDeploymentsView,
  UpdateAiModelsDto,
  UpdatePlatformSettingsDto,
  GenerateAssetDto,
  GenerateContentDto,
  GeneratedContent,
  ConvertToThreadDto,
  ThreadConversion,
  Post,
  ApplyMediaToSetResult,
  CopyDestinationResult,
  PostGroup,
  RegisteredEntraGroup,
  RegisterEntraGroupDto,
  Source,
  SourceItem,
  SourceItemPage,
  SourcePull,
  SocialConnection,
  SocialAppKeysStatus,
  SocialAppKeysView,
  SaveSocialAppKeysDto,
  SocialCredentialSet,
  AnalyticsOverview,
  PlatformPerformance,
  TopPosts,
  TopPostSort,
  InboxConversation,
  SocialPlatform,
  ConnectSocialDto,
  ReconnectSocialDto,
  SaveApproverAssignmentDto,
  SaveMediaCredentialDto,
  ChatFeedback,
  ChatMessage,
  SendMessageResult,
  ApplyScheduleToSetDto,
  ApplyScheduleToSetResult,
  SeededStrategistChat,
  StrategistConversation,
  StrategistConversationWithMessages,
  SubmitForApprovalDto,
  TeammateTopic,
  Tenant,
  TenantMembership,
  TrendTopic,
  UpdateAssetDto,
  UpdateAssetFolderDto,
  UpdateBrandVoiceDto,
  UpdateCampaignDto,
  UpdateContentDto,
  UpdatePostDto,
  UpdateSourceDto,
  UpdateTenantDto,
  UpdateUserSettingsDto,
  UpsertMembershipDto,
  User,
  WebhookTestResult,
  WorkspaceNameConflict,
} from '@mg/shared';
import { AssetGenerator, BRAND_API_ERRORS, SOCIAL_APP_KEYS_NOT_SET } from '@mg/shared';
import { env } from './env';
import { captureVideoPoster } from './videoPoster';

/** Error thrown for any non-2xx API response, carrying the parsed envelope. */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly body: ApiError | undefined;

  constructor(status: number, message: string, body?: ApiError) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.body = body;
  }
}

/**
 * Narrow an error to a Brand Workspace name-conflict 409 (carrying the existing workspace's
 * admins to contact), or null. Used by the create/edit workspace forms to render contacts.
 */
export function asWorkspaceNameConflict(err: unknown): WorkspaceNameConflict | null {
  if (
    err instanceof ApiRequestError &&
    err.status === 409 &&
    err.body &&
    (err.body as { code?: string }).code === 'WORKSPACE_NAME_TAKEN'
  ) {
    return err.body as unknown as WorkspaceNameConflict;
  }
  return null;
}

/**
 * A start or reconnect refused because the install has no app keys for its Site Integrations
 * credential set (409 `SOCIAL_APP_KEYS_NOT_SET`). The machine code rides in `error`, the one custom
 * field the API's error filter keeps.
 */
export function isSocialAppKeysNotSet(err: unknown): boolean {
  return err instanceof ApiRequestError && err.status === 409 && err.body?.error === SOCIAL_APP_KEYS_NOT_SET;
}

/**
 * A brand save refused because the brand changed in another tab or by another admin since the page
 * read it (409 `BRAND_VERSION_CONFLICT`): the page offers Reload. Its `message` is the sentence to show.
 */
export function isBrandVersionConflict(err: unknown): boolean {
  return (
    err instanceof ApiRequestError && err.status === 409 && err.body?.error === BRAND_API_ERRORS.VERSION_CONFLICT
  );
}

/**
 * User-facing text for an action error. A 401 means the app SESSION expired (not
 * anything the user typed), so surface a "refresh" hint instead of the raw
 * "Missing or invalid credentials" — which reads as a bad-credentials error.
 */
export function actionErrorText(error: unknown, fallback: string): string {
  if (error instanceof ApiRequestError) {
    if (error.status === 401) {
      return 'Your session expired — please refresh the page and try again.';
    }
    return error.message;
  }
  return fallback;
}

/**
 * Pluggable providers wired by the auth/tenant layers at runtime so the client
 * stays framework-agnostic and dependency-free.
 */
type TokenProvider = () => Promise<string | null>;
type TenantProvider = () => string | null;

let getAccessToken: TokenProvider = async () => null;
let getActiveTenant: TenantProvider = () => null;
/** Force-refresh the access token (bypass cache) after a 401. Returns null if unavailable. */
let refreshAccessToken: TokenProvider = async () => null;

export function configureApi(providers: {
  token: TokenProvider;
  tenant: TenantProvider;
  refresh?: TokenProvider;
}): void {
  getAccessToken = providers.token;
  getActiveTenant = providers.tenant;
  if (providers.refresh) refreshAccessToken = providers.refresh;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  /** Skip the X-Tenant header (auth/tenants endpoints are not tenant-scoped). */
  skipTenant?: boolean;
  /** Skip the Authorization header (public endpoints like magic-link). */
  skipAuth?: boolean;
  signal?: AbortSignal;
  /** @internal — marks the single retry after a 401 silent-refresh (prevents loops). */
  retried?: boolean;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(`${env.apiBaseUrl}/api${path}`);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined) url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, skipTenant, skipAuth, signal } = options;

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  if (!skipAuth) {
    const token = await getAccessToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  if (!skipTenant) {
    const tenant = getActiveTenant();
    if (tenant) headers['X-Tenant'] = tenant;
  }

  let res: Response;
  try {
    res = await fetch(buildUrl(path, query), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: 'include',
      signal,
    });
  } catch {
    throw new ApiRequestError(0, 'Network error — is the API running?', undefined);
  }

  // Self-heal an expired-token 401: force a fresh access token and retry ONCE. If the
  // session is truly gone, refreshAccessToken bounces to sign-in and returns null, so we
  // fall through and surface the 401 rather than loop.
  if (res.status === 401 && !skipAuth && !options.retried) {
    const fresh = await refreshAccessToken();
    if (fresh) return request<T>(path, { ...options, retried: true });
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const parsed: unknown = text ? JSON.parse(text) : undefined;

  if (!res.ok) {
    const errBody = parsed as ApiError | undefined;
    const message = errBody
      ? Array.isArray(errBody.message)
        ? errBody.message.join(', ')
        : errBody.message
      : res.statusText;
    throw new ApiRequestError(res.status, message, errBody);
  }

  return parsed as T;
}

/** Build the standard auth + tenant headers (used by non-JSON requests too). */
async function authHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {};
  const token = await getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const tenant = getActiveTenant();
  if (tenant) headers['X-Tenant'] = tenant;
  return headers;
}

/**
 * Auth+tenant fetch with a single force-refresh retry on 401 — the same self-heal
 * as request(), for the binary/multipart helpers (avatar, asset image, upload) so a
 * server-rejected token recovers instead of failing the image/upload.
 */
async function authFetch(url: string, init: RequestInit): Promise<Response> {
  const doFetch = async (): Promise<Response> => {
    const given = init.headers as Record<string, string> | undefined;
    const headers = {
      ...given,
      ...(await authHeaders()),
      // A caller may name the workspace explicitly (another workspace's asset, e.g. its logo in the
      // workspace switcher); the sign-in itself always comes from authHeaders.
      ...(given?.['X-Tenant'] ? { 'X-Tenant': given['X-Tenant'] } : {}),
    };
    return fetch(url, { ...init, headers, credentials: 'include' });
  };
  let res: Response;
  try {
    res = await doFetch();
  } catch {
    throw new ApiRequestError(0, 'Network error — is the API running?', undefined);
  }
  if (res.status === 401) {
    const fresh = await refreshAccessToken();
    if (fresh) {
      try {
        res = await doFetch();
      } catch {
        throw new ApiRequestError(0, 'Network error — is the API running?', undefined);
      }
    }
  }
  return res;
}

/**
 * Fetch a binary resource (e.g. the current user's avatar) with the bearer
 * token attached. Returns a Blob, or null on 404 (no avatar set).
 */
async function requestBlob(path: string, signal?: AbortSignal): Promise<Blob | null> {
  const res = await authFetch(buildUrl(path), { method: 'GET', signal });
  if (res.status === 404) return null;
  if (!res.ok) {
    throw new ApiRequestError(res.status, res.statusText, undefined);
  }
  return res.blob();
}

/**
 * Fetch a binary resource by its **full proxy URL** (e.g. an asset's `blobUrl`
 * like `/api/assets/:id/image`, already including the `/api` prefix) with the
 * bearer token attached. Relative URLs resolve against `apiBaseUrl`; absolute
 * (external) URLs are fetched as-is. Returns a Blob, or null on 404.
 */
async function requestBlobUrl(
  proxyUrl: string,
  signal?: AbortSignal,
  /** The workspace the asset belongs to, when it is not the active one (asset routes are workspace-scoped). */
  tenantSlug?: string,
): Promise<Blob | null> {
  const isAbsolute = /^https?:\/\//i.test(proxyUrl);
  const url = isAbsolute ? proxyUrl : `${env.apiBaseUrl}${proxyUrl}`;
  const res = await authFetch(url, {
    method: 'GET',
    signal,
    headers: tenantSlug ? { 'X-Tenant': tenantSlug } : undefined,
  });
  if (res.status === 404) return null;
  if (!res.ok) {
    throw new ApiRequestError(res.status, res.statusText, undefined);
  }
  return res.blob();
}

/**
 * POST a JSON body and read the response back as a binary Blob plus the filename the server
 * chose in `Content-Disposition` (bulk ZIP download). POST rather than GET because the body is a
 * selection of ids that would overflow a URL. Errors arrive as JSON, so they are parsed for a
 * real message before the blob path is taken.
 */
async function requestZip(
  path: string,
  body: unknown,
): Promise<{ blob: Blob; filename: string | null }> {
  const res = await authFetch(buildUrl(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let message = res.statusText;
    try {
      const errBody = (await res.json()) as ApiError | undefined;
      if (errBody?.message) {
        message = Array.isArray(errBody.message) ? errBody.message.join(', ') : errBody.message;
      }
    } catch {
      // non-JSON error body — keep the status text
    }
    throw new ApiRequestError(res.status, message, undefined);
  }
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const match = /filename="?([^"]+)"?/i.exec(disposition);
  return { blob: await res.blob(), filename: match?.[1] ?? null };
}

/**
 * POST multipart/form-data (e.g. avatar upload). Lets the browser set the
 * Content-Type boundary; only the auth + tenant headers are added.
 */
/**
 * Attach a still frame when the file being uploaded is a video.
 *
 * Silent on failure — an undecodable codec must not turn into a failed upload. The asset simply has
 * no poster and its tile shows the media-type icon, exactly as every video did before.
 */
async function appendVideoPoster(form: FormData, file: File): Promise<void> {
  try {
    const poster = await captureVideoPoster(file);
    if (poster) form.append('poster', poster, 'poster.jpg');
  } catch {
    // Best-effort by design.
  }
}

/**
 * POST multipart with UPLOAD PROGRESS and cancellation, via XHR.
 *
 * `fetch` cannot report how much of a request body has been sent — there is no upload-progress
 * event and a streaming request body needs duplex support the browsers we target don't give us. So
 * the one place that needs a real percentage uses XMLHttpRequest, and everything else keeps using
 * `authFetch`.
 *
 * It lives here, next to `authFetch`, so it can reuse the same auth: the bearer + tenant headers and
 * the single force-refresh retry on 401. An upload can take minutes, which makes it MORE likely than
 * an ordinary request to outlive its access token, not less — a copy of this without the retry would
 * fail exactly the uploads that took the longest.
 */
async function requestMultipartWithProgress<T>(
  path: string,
  form: FormData,
  opts: { onProgress?: (fraction: number) => void; signal?: AbortSignal },
): Promise<T> {
  const send = (headers: Record<string, string>): Promise<{ status: number; body: string }> =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', buildUrl(path));
      xhr.withCredentials = true;
      for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);

      if (opts.onProgress) {
        xhr.upload.onprogress = (e) => {
          // `lengthComputable` is false for a chunked body; report nothing rather than a made-up
          // number, and let the caller show an indeterminate state.
          if (e.lengthComputable && e.total > 0) opts.onProgress?.(e.loaded / e.total);
        };
      }

      xhr.onload = () => resolve({ status: xhr.status, body: xhr.responseText });
      xhr.onerror = () => reject(new ApiRequestError(0, 'Network error — is the API running?'));
      xhr.onabort = () => reject(new DOMException('Upload cancelled', 'AbortError'));

      if (opts.signal) {
        if (opts.signal.aborted) {
          xhr.abort();
          return;
        }
        opts.signal.addEventListener('abort', () => xhr.abort(), { once: true });
      }
      xhr.send(form);
    });

  let res = await send(await authHeaders());
  if (res.status === 401 && (await refreshAccessToken())) {
    res = await send(await authHeaders());
  }

  const parsed: unknown = res.body ? JSON.parse(res.body) : undefined;
  if (res.status < 200 || res.status >= 300) {
    const errBody = parsed as ApiError | undefined;
    const message = errBody
      ? Array.isArray(errBody.message)
        ? errBody.message.join(', ')
        : errBody.message
      : `Upload failed (${res.status})`;
    throw new ApiRequestError(res.status, message, errBody);
  }
  return parsed as T;
}

async function requestMultipart<T>(path: string, form: FormData): Promise<T> {
  const res = await authFetch(buildUrl(path), { method: 'POST', body: form });
  const text = await res.text();
  const parsed: unknown = text ? JSON.parse(text) : undefined;
  if (!res.ok) {
    const errBody = parsed as ApiError | undefined;
    const message = errBody
      ? Array.isArray(errBody.message)
        ? errBody.message.join(', ')
        : errBody.message
      : res.statusText;
    throw new ApiRequestError(res.status, message, errBody);
  }
  return parsed as T;
}

/** What the person changed about a re-read's sources in Revisit step 1 (api.startBrandReread). */
export interface RereadSources {
  /** Replaces the stored document. */
  file?: File;
  /** Reads no document this time (ignored with a `file`). */
  removeDocument?: boolean;
  /** The whole list of web pages to read, normalized (scanUrlsFromRows). Absent: the stored pages. */
  urls?: readonly string[];
}

/**
 * A member of the active workspace as returned by `GET /api/users` — the user
 * record plus their membership/roles in the active tenant. Used by the approver
 * picker in App Settings.
 */
export interface WorkspaceMember {
  user: User;
  membership: TenantMembership;
}

/** Typed surface over the Smorgas Social REST API. */
export const api = {
  // ---- auth ----
  me: (signal?: AbortSignal) => request<AuthPrincipal>('/auth/me', { skipTenant: true, signal }),

  // ---- current user (me) settings + avatar ----
  /** Update the signed-in user's own settings (personal AI voice, topic sharing). */
  updateMeSettings: (dto: UpdateUserSettingsDto) =>
    request<User>('/me/settings', { method: 'PUT', body: dto, skipTenant: true }),

  /** Send a test event to the SITE-wide integration webhook. Tenant-Admin only. */
  testWebhook: () =>
    request<WebhookTestResult>('/platform-settings/webhook-test', {
      method: 'POST',
      skipTenant: true,
    }),

  /** Fetch the signed-in user's custom avatar image (auth-protected). null if unset. */
  fetchAvatarBlob: (signal?: AbortSignal) => requestBlob('/me/avatar', signal),

  /** Upload a new custom avatar image (multipart, field `file`). */
  uploadAvatar: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return requestMultipart<{ avatarUrl: string }>('/me/avatar', form);
  },

  requestMagicLink: (email: string, tenant?: string) =>
    request<{ sent: true; code?: string }>('/auth/magic-link/request', {
      method: 'POST',
      body: { email, tenant },
      skipTenant: true,
      skipAuth: true,
    }),

  verifyMagicLink: (email: string, code: string, tenant?: string) =>
    request<{ accessToken: string; principal: AuthPrincipal }>('/auth/magic-link/verify', {
      method: 'POST',
      body: { email, code, tenant },
      skipTenant: true,
      skipAuth: true,
    }),

  // ---- tenants ----
  listTenants: (signal?: AbortSignal) =>
    request<Tenant[]>('/tenants', { skipTenant: true, signal }),

  createTenant: (dto: CreateTenantDto) =>
    request<Tenant>('/tenants', { method: 'POST', body: dto, skipTenant: true }),

  updateTenant: (id: string, dto: UpdateTenantDto) =>
    request<Tenant>(`/tenants/${id}`, { method: 'PATCH', body: dto, skipTenant: true }),

  /**
   * Permanently delete a Brand Workspace and all its content/campaigns/posts/sources/members.
   * Workspace-Admin-only (of that workspace). skipTenant — identified by path id, not the header.
   */
  deleteTenant: (id: string) =>
    request<void>(`/tenants/${id}`, { method: 'DELETE', skipTenant: true }),

  /**
   * Upload a brand-guidelines doc (multipart, field `file`; PDF/DOCX) and get back
   * suggested workspace fields to prefill the edit form. May be `{}` when nothing
   * could be extracted; throws ApiRequestError for a bad file type / too large / parse failure.
   */
  extractBrandGuidelines: (tenantId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return requestMultipart<BrandGuidelinesExtraction>(`/tenants/${tenantId}/brand-guidelines`, form);
  },

  // ---- Brand Workspace v2 (behind the install's flag) ----
  // Every route names its workspace in the path and is not scoped by the X-Tenant header (E20): the
  // page may show a workspace other than the active one. While the flag is off the routes below
  // answer 404 (BRAND_WORKSPACE_V2_OFF), except the flag itself. A 400 or 409 carries the sentence to
  // show as its message.

  /** Whether Brand Workspace v2 is on for this install. Any signed-in user, with a workspace or not. */
  getBrandWorkspaceFlag: (signal?: AbortSignal) =>
    request<BrandWorkspaceFlagView>('/brand-workspace/flag', { skipTenant: true, signal }),

  /** A workspace's brand information. Any member, or a Tenant Admin. Never writes. */
  getBrandProfile: (tenantId: string, signal?: AbortSignal) =>
    request<BrandProfileView>(`/tenants/${tenantId}/brand`, { skipTenant: true, signal }),

  /**
   * The Edit page's Brand information save: name (only when changed), website and the editable lists.
   * Admins. Adopt the returned view (its `version`) before the next save.
   */
  saveBrandInformation: (tenantId: string, dto: BrandInformationSaveRequest) =>
    request<BrandProfileView>(`/tenants/${tenantId}/brand/information`, {
      method: 'PUT',
      body: dto,
      skipTenant: true,
    }),

  /** Saves one Brand Setup step (its own fields only) and where Continue reopens. Admins. */
  saveOnboardingStep: (tenantId: string, step: BrandWizardStep, dto: OnboardingStepSaveRequest) =>
    request<BrandProfileView>(`/tenants/${tenantId}/brand/onboarding/steps/${step}`, {
      method: 'PUT',
      body: dto,
      skipTenant: true,
    }),

  /** Moves where Continue Brand Setup reopens, saving no field (Skip for now, Exit without saving). Admins. */
  saveOnboardingPosition: (tenantId: string, dto: OnboardingPositionRequest) =>
    request<BrandOnboardingView>(`/tenants/${tenantId}/brand/onboarding/position`, {
      method: 'PUT',
      body: dto,
      skipTenant: true,
    }),

  /** The other workspaces Brand Setup step 2 may copy Company voice and identity from. Admins of `tenantId`. */
  listBrandCopySources: (tenantId: string, signal?: AbortSignal) =>
    request<BrandCopySource[]>(`/tenants/${tenantId}/brand/copy-sources`, { skipTenant: true, signal }),

  /** The Company voice and identity text of the workspace to copy from (its members, or a Tenant Admin). */
  getCompanyVoiceForCopy: (sourceTenantId: string) =>
    request<BrandCompanyVoiceSource>(`/tenants/${sourceTenantId}/brand/company-voice`, { skipTenant: true }),

  /** Stops the AI using the unsorted previous guidance and hides its panel. Admins. */
  dismissLegacyGuidance: (tenantId: string) =>
    request<BrandProfileView>(`/tenants/${tenantId}/brand/legacy-guidance/dismiss`, {
      method: 'POST',
      skipTenant: true,
    }),

  /** Closes the one-time "We've sorted your previous guidance…" notice. Admins. */
  dismissMigrationNotice: (tenantId: string) =>
    request<BrandProfileView>(`/tenants/${tenantId}/brand/migration-notice/dismiss`, {
      method: 'POST',
      skipTenant: true,
    }),

  /**
   * Uploads the brand document (.docx or PDF, multipart field `file`) and, from Release 4, names the web
   * pages to scan (multipart field `urls`: a JSON array of addresses as text, normalized and at most
   * BRAND_LIMITS.urls, as normalizeWebsiteUrlList gives them), and starts reading them into a draft (Brand
   * Setup step 1). Either or both. Reports upload progress; nothing is saved to the workspace. Admins.
   * Refusals come with their sentence (400 unsupported file or invalid addresses, 413 too large, 429 too
   * many, 503 no AI service).
   */
  startBrandImport: (
    tenantId: string,
    source: { file?: File; urls?: readonly string[] },
    opts: { onProgress?: (fraction: number) => void; signal?: AbortSignal } = {},
  ) => {
    const form = new FormData();
    if (source.file) form.append('file', source.file);
    // Left out when there are none: a Release 3 server refuses the field whatever it holds.
    if (source.urls && source.urls.length > 0) form.append('urls', JSON.stringify(source.urls));
    return requestMultipartWithProgress<StartBrandImportResponse>(`/tenants/${tenantId}/brand-import/jobs`, form, opts);
  },

  /**
   * Revisit Brand Setup's "Re-read my sources" (Release 4): reads the workspace's document and web pages
   * again into a proposal. With no `sources` it reads the stored ones as they are (JSON, no upload). With
   * `sources` (what the person changed in step 1) it is multipart: a new `file` replaces the document,
   * `removeDocument` reads none, and `urls` is the whole page list (`[]` included). Poll the job; nothing is
   * saved to the workspace. Admins. 409 BRAND_REREAD_NO_SOURCES when nothing is stored to read; 400
   * BRAND_IMPORT_NO_SOURCE when the changes left nothing.
   */
  startBrandReread: (
    tenantId: string,
    sources?: RereadSources,
    opts: { onProgress?: (fraction: number) => void; signal?: AbortSignal } = {},
  ) => {
    if (!sources) {
      return request<StartBrandImportResponse>(`/tenants/${tenantId}/brand-import/jobs`, {
        method: 'POST',
        body: { purpose: 'reread' } satisfies StartBrandRereadRequest,
        skipTenant: true,
      });
    }
    const form = new FormData();
    form.append('purpose', 'reread');
    if (sources.file) form.append('file', sources.file);
    else if (sources.removeDocument) form.append('removeDocument', 'true');
    if (sources.urls) form.append('urls', JSON.stringify(sources.urls));
    return requestMultipartWithProgress<StartBrandImportResponse>(`/tenants/${tenantId}/brand-import/jobs`, form, opts);
  },

  /**
   * A finished re-read's proposal against the saved values it started from (Show changes, Release 4): text
   * fields word by word; tag lists and Value philosophy as values to compare. Admins.
   */
  getBrandImportChanges: (tenantId: string, jobId: string, signal?: AbortSignal) =>
    request<BrandImportChangesView>(`/tenants/${tenantId}/brand-import/jobs/${jobId}/changes`, {
      skipTenant: true,
      signal,
    }),

  /**
   * Regenerate (Release 4): the same re-read runs again on the same sources for a new proposal (its id comes
   * back; poll it, then ask for its changes again). Admins.
   */
  regenerateBrandReread: (tenantId: string, jobId: string) =>
    request<StartBrandImportResponse>(`/tenants/${tenantId}/brand-import/jobs/${jobId}/regenerate`, {
      method: 'POST',
      skipTenant: true,
    }),

  /**
   * AI suggestions for empty Brand Setup fields (Release 4), from what the wizard holds now (sent, saved or
   * not), the latest sources, then the name and website. Never saved: the customer clicks Use suggestion
   * and saves the step. Admins.
   */
  getBrandSuggestions: (tenantId: string, dto: BrandSuggestionRequest, signal?: AbortSignal) =>
    request<BrandSuggestionsResponse>(`/tenants/${tenantId}/brand/suggestions`, {
      method: 'POST',
      body: dto,
      skipTenant: true,
      signal,
    }),

  /** The workspace's newest import, or `{ job: null }`. Any reader; `draft` only for admins. */
  getLatestBrandImport: (tenantId: string, signal?: AbortSignal) =>
    request<BrandImportLatestResponse>(`/tenants/${tenantId}/brand-import/jobs/latest`, { skipTenant: true, signal }),

  /** One import job, polled while it runs. Any reader; `draft` only for admins. */
  getBrandImportJob: (tenantId: string, jobId: string, signal?: AbortSignal) =>
    request<BrandImportJobView>(`/tenants/${tenantId}/brand-import/jobs/${jobId}`, { skipTenant: true, signal }),

  /** Try again after an AI failure: the same job runs again (its id comes back). Admins. */
  retryBrandImport: (tenantId: string, jobId: string) =>
    request<StartBrandImportResponse>(`/tenants/${tenantId}/brand-import/jobs/${jobId}/retry`, {
      method: 'POST',
      skipTenant: true,
    }),

  /**
   * The original file of an import job, as a Blob, for any reader. Rejects with a 404 ApiRequestError
   * once a newer upload has replaced it.
   */
  downloadBrandDocument: async (tenantId: string, jobId: string): Promise<Blob> => {
    const blob = await requestBlob(`/tenants/${tenantId}/brand-import/jobs/${jobId}/document`);
    if (!blob) throw new ApiRequestError(404, 'This document is no longer available.');
    return blob;
  },

  /**
   * The Brand assets and style page's save: colors, typography and logos ONLY. Social hashtags and
   * Catchphrases live in the Brand information page's Vocabulary, and while the flag is on the server
   * merges these three keys into the stored brand assets, so a page holding an older copy of the
   * hashtags can never write them back (E4). The three keys are picked here, whatever else the
   * caller's object holds.
   */
  saveBrandAssetsStyle: (tenantId: string, style: Pick<BrandAssets, 'colors' | 'typography' | 'logos'>) =>
    request<Tenant>(`/tenants/${tenantId}`, {
      method: 'PATCH',
      body: { brandAssets: { colors: style.colors, typography: style.typography, logos: style.logos } },
      skipTenant: true,
    }),

  // ---- platform settings (install-wide approval policy + site integrations) ----
  /**
   * Re-run OAuth for an existing connection with the install's Site Integrations app for its
   * credential set (409 SOCIAL_APP_KEYS_NOT_SET while it has none); an Instagram Login connection
   * uses its own stored app. Returns the provider URL to send the browser to.
   *
   * `dto` asks for more as well (Threads `threadReplies`/`threadMentions`, Instagram `inbox`,
   * Facebook features); a routine Reconnect sends no body, and keeps whatever the connection has.
   */
  reconnectSocial: (connectionId: string, dto?: ReconnectSocialDto) =>
    request<{ url: string }>(`/social/connections/${connectionId}/reconnect`, {
      method: 'POST',
      body: dto,
    }),

  // Trash: soft-deleted campaigns + posts, 30-day retention.
  getTrash: (signal?: AbortSignal) => request<TrashItem[]>('/trash', { signal }),
  restoreTrash: (dto: TrashActionDto) =>
    request<void>('/trash/restore', { method: 'POST', body: dto }),
  purgeTrash: (dto: TrashActionDto) =>
    request<void>('/trash/purge', { method: 'POST', body: dto }),

  /** Read the install-level PlatformSettings singleton. Any member. */
  getPlatformSettings: (signal?: AbortSignal) =>
    request<PlatformSettings>('/platform-settings', { skipTenant: true, signal }),
  /**
   * Update the install-level PlatformSettings — approval policy AND/OR the site
   * integrations config (webhookUrl / apifyApiKey / apifyUserId / figmaPat / canva*).
   * Tenant-Admin only (403 otherwise).
   */
  updatePlatformSettings: (dto: UpdatePlatformSettingsDto) =>
    request<PlatformSettings>('/platform-settings', {
      method: 'PATCH',
      body: dto,
      skipTenant: true,
    }),

  /**
   * Which directory this install's customer signs in from. Tenant-Admin only (403 otherwise).
   * Never returns the Google client secret - only whether one is stored.
   */
  getCustomerDirectory: (signal?: AbortSignal) =>
    request<CustomerDirectoryView>('/platform-settings/customer-directory', {
      skipTenant: true,
      signal,
    }),
  /** Save what changed; omitted fields are left alone, null clears a field. Tenant-Admin only. */
  updateCustomerDirectory: (dto: UpdateCustomerDirectoryDto) =>
    request<CustomerDirectoryView>('/platform-settings/customer-directory', {
      method: 'PATCH',
      body: dto,
      skipTenant: true,
    }),
  /** Which deployment each AI feature uses on this install, with each value's source. Tenant-Admin only. */
  getAiModels: (signal?: AbortSignal) =>
    request<AiModelsView>('/platform-settings/ai-models', { skipTenant: true, signal }),
  /** This install's real deployments, live from Azure, per slot. Tenant-Admin only; 503 with a sentence when Azure can't be asked. */
  getAiModelDeployments: (signal?: AbortSignal) =>
    request<AiDeploymentsView>('/platform-settings/ai-models/deployments', { skipTenant: true, signal }),
  /** Save what changed: a name, null = back to the install setting, imageOff:true = image generation off. Tenant-Admin only. */
  updateAiModels: (dto: UpdateAiModelsDto) =>
    request<AiModelsView>('/platform-settings/ai-models', { method: 'PATCH', body: dto, skipTenant: true }),
  /**
   * Read the MASKED site-integrations view (webhookUrl plus apifyKeySet / figmaPatSet /
   * canvaClientSecretSet booleans — the raw secret keys are never returned).
   * Install-wide (skipTenant); Tenant-Admin only (403 otherwise).
   */
  // ── Billing (install-level, Tenant-Admin) ────────────────────────────────
  getBilling: (signal?: AbortSignal) =>
    request<BillingSummaryDto>('/billing', { skipTenant: true, signal }),
  getBillingPlans: (signal?: AbortSignal) =>
    request<PlanDto[]>('/billing/plans', { skipTenant: true, signal }),
  getBillingMembers: (signal?: AbortSignal) =>
    request<SeatMemberDto[]>('/billing/members', { skipTenant: true, signal }),
  getBillingInvoices: (signal?: AbortSignal) =>
    request<InvoiceDto[]>('/billing/invoices', { skipTenant: true, signal }),
  startCheckout: (planId: string) =>
    request<CheckoutSessionResultDto>('/billing/checkout-session', {
      method: 'POST',
      body: { planId },
      skipTenant: true,
    }),
  openBillingPortal: () =>
    request<PortalSessionResultDto>('/billing/portal-session', { method: 'POST', skipTenant: true }),
  setSeatPolicy: (dto: SeatPolicyRequestDto) =>
    request<BillingSummaryDto>('/billing/seat-policy', { method: 'PUT', body: dto, skipTenant: true }),
  // ── Billing Admin (email-allowlisted) ─────────────────────────────────────
  getOneTimeFees: (signal?: AbortSignal) =>
    request<OneTimeFeeDto[]>('/billing/admin/fees', { skipTenant: true, signal }),
  chargeOneTimeFee: (planId: string, justification?: string) =>
    request<OneTimeFeeDto[]>(`/billing/admin/fees/${planId}/charge`, {
      method: 'POST',
      body: { justification },
      skipTenant: true,
    }),
  cancelFeeInvoice: (planId: string, invoiceId: string) =>
    request<OneTimeFeeDto[]>(
      `/billing/admin/fees/${planId}/invoices/${encodeURIComponent(invoiceId)}/cancel`,
      { method: 'POST', skipTenant: true },
    ),
  resolveFeeCharge: (planId: string, chargeId: string) =>
    request<OneTimeFeeDto[]>(
      `/billing/admin/fees/${planId}/charges/${encodeURIComponent(chargeId)}/resolve`,
      { method: 'POST', skipTenant: true },
    ),
  getTenantAccess: (signal?: AbortSignal) =>
    request<TenantAccessDto>('/billing/access', { skipTenant: true, signal }),
  setTenantAccess: (suspended: boolean, note?: string) =>
    request<TenantAccessDto>('/billing/admin/access', {
      method: 'PUT',
      body: { suspended, note },
      skipTenant: true,
    }),
  getSeatUsage: (signal?: AbortSignal) =>
    request<SeatUsageDto>('/billing/seats', { skipTenant: true, signal }),
  getBillingSchedule: (signal?: AbortSignal) =>
    request<BillingScheduleDto>('/billing/admin/schedule', { skipTenant: true, signal }),
  setBillingSchedule: (billingCycleAnchor: string | null) =>
    request<BillingScheduleDto>('/billing/admin/schedule', {
      method: 'PUT',
      body: { billingCycleAnchor },
      skipTenant: true,
    }),
  /** Which install this Billing Admin page is administering. */
  getInstallIdentity: (signal?: AbortSignal) =>
    request<InstallIdentityDto>('/billing/admin/install', { skipTenant: true, signal }),

  getBillingAdminAccess: (signal?: AbortSignal) =>
    request<BillingAdminAccessDto>('/billing/admin/access', { skipTenant: true, signal }),
  grantBillingAdmin: (oid: string) =>
    request<BillingAdminAccessDto>('/billing/admin/access', {
      method: 'POST',
      body: { oid },
      skipTenant: true,
    }),
  revokeBillingAdmin: (email: string) =>
    request<BillingAdminAccessDto>(`/billing/admin/access/${encodeURIComponent(email)}`, {
      method: 'DELETE',
      skipTenant: true,
    }),
  getAdminPlans: (signal?: AbortSignal) =>
    request<AdminPlanDto[]>('/billing/admin/plans', { skipTenant: true, signal }),
  setPlanExposure: (planId: string, isSelectable: boolean) =>
    request<AdminPlanDto[]>(`/billing/admin/plans/${planId}/exposure`, {
      method: 'PUT',
      body: { isSelectable },
      skipTenant: true,
    }),
  syncBillingPlans: () =>
    request<PlanSyncResultDto>('/billing/plans/sync', { method: 'POST', skipTenant: true }),
  getSiteIntegrations: (signal?: AbortSignal) =>
    request<SiteIntegrationsView>('/platform-settings/integrations', {
      skipTenant: true,
      signal,
    }),
  /**
   * The Apify account (username + id) behind the currently-configured key, looked up live from
   * Apify so a Tenant Admin can confirm WHICH account is active. Install-wide (skipTenant);
   * Tenant-Admin only. Never returns the key.
   */
  getApifyIdentity: (signal?: AbortSignal) =>
    request<ApifyIdentity>('/platform-settings/apify-identity', {
      skipTenant: true,
      signal,
    }),
  /**
   * The Figma account (handle + email) behind the currently-configured personal access token,
   * looked up live from Figma so a Tenant Admin can confirm WHICH account is active — the
   * write-only PAT can't reveal that. Install-wide (skipTenant); Tenant-Admin only.
   */
  getFigmaIdentity: (signal?: AbortSignal) =>
    request<FigmaIdentity>('/platform-settings/figma/identity', {
      skipTenant: true,
      signal,
    }),
  /**
   * Recent install-level (site-wide) audit entries — settings/integration changes, media
   * credential saves/removals, Tenant-Admin grants/revokes. Install-wide (skipTenant);
   * Tenant-Admin only (403 otherwise). Never contains secret values.
   */
  getSiteAudit: (signal?: AbortSignal) =>
    request<SiteAuditEntry[]>('/platform-settings/audit', {
      skipTenant: true,
      signal,
    }),

  // ---- Social Media Integrations (the install's social developer apps, on Site Integrations) ----
  /**
   * Every credential set as a Tenant Admin sees it: the client id in full, whether a secret is saved
   * (never the secret), and the callback URL its flow sends. Install-wide (skipTenant); Tenant-Admin
   * only.
   */
  getSocialAppKeys: (signal?: AbortSignal) =>
    request<SocialAppKeysView>('/site-integrations/social-app-keys', {
      skipTenant: true,
      signal,
    }),
  /**
   * Add or replace one set's keys. Omitted values keep what is stored; the first save of a set needs
   * both. Answers with the whole view. Install-wide (skipTenant); Tenant-Admin only.
   */
  saveSocialAppKeys: (set: SocialCredentialSet, dto: SaveSocialAppKeysDto) =>
    request<SocialAppKeysView>(`/site-integrations/social-app-keys/${set}`, {
      method: 'PATCH',
      body: dto,
      skipTenant: true,
    }),
  /**
   * Remove one set's keys. Accounts already connected keep working; nobody can start a new connection
   * or reconnect one that uses them until keys are added again. Answers with the whole view.
   * Install-wide (skipTenant); Tenant-Admin only.
   */
  removeSocialAppKeys: (set: SocialCredentialSet) =>
    request<SocialAppKeysView>(`/site-integrations/social-app-keys/${set}`, {
      method: 'DELETE',
      skipTenant: true,
    }),
  /**
   * Whether each set has both values saved - what gates Connect, Add Additional, Reconnect and the
   * one-click feature buttons on the Connections page. Booleans only. Install-wide (skipTenant); any
   * signed-in user.
   */
  getSocialAppKeysStatus: (signal?: AbortSignal) =>
    request<SocialAppKeysStatus>('/site-integrations/social-app-keys/status', {
      skipTenant: true,
      signal,
    }),

  // ---- workspace members (approver picker, member listings) ----
  /** Members of the active workspace with their access role + Tenant-Admin flag. */
  listMembers: (signal?: AbortSignal) => request<WorkspaceMember[]>('/users', { signal }),
  /**
   * Every user the caller manages, deduped, each with the memberships that fall within the
   * caller's manageable workspaces. Tenant Admin → the whole install; Workspace Admin → users
   * in the workspaces they administer. Not tenant-scoped (spans workspaces).
   */
  getManagedMembers: (signal?: AbortSignal) =>
    request<ManagedMember[]>('/users/managed-members', { skipTenant: true, signal }),
  /** Brand Workspaces the caller can manage (member-management pickers). Not tenant-scoped. */
  getManageableWorkspaces: (signal?: AbortSignal) =>
    request<ManageableWorkspace[]>('/users/manageable-workspaces', { skipTenant: true, signal }),
  /** Remove a user from ONE workspace (204 on success). Workspace Admin of that workspace. */
  removeMembershipFromWorkspace: (userId: string, tenantId: string) =>
    request<void>(`/users/${userId}/memberships/${tenantId}`, {
      method: 'DELETE',
      skipTenant: true,
    }),
  /**
   * Add a member by email, or change an existing member's workspace role. When `tenantIds`
   * is present (a Tenant-Admin cross-workspace action) the member is upserted into EACH of
   * those workspaces; when absent, only the active workspace. Workspace Admin only.
   */
  upsertMembership: (dto: UpsertMembershipDto) =>
    request<WorkspaceMember>('/users/memberships', { method: 'PUT', body: dto }),
  /** Remove a member from the active workspace (204 on success). Workspace Admin only. */
  removeMembership: (userId: string) =>
    request<void>(`/users/${userId}/membership`, { method: 'DELETE' }),
  /**
   * Invite someone from outside the organization as an approver of ONE workspace (`dto.tenantId`,
   * which the caller must administer - not the active one). `portalEnabled` in the answer says
   * whether this site's approval portal can deliver a sign-in code, i.e. whether a link is worth
   * handing out.
   */
  inviteExternalApprover: (dto: InviteExternalApproverDto) =>
    request<InviteExternalApproverResult>('/users/external-approvers', {
      method: 'POST',
      body: dto,
      skipTenant: true,
    }),
  /**
   * Grant or revoke a user's install-wide Tenant Admin flag. Tenant-Admin-only; the API
   * guards the last remaining Tenant Admin. Returns the updated user.
   */
  setTenantAdmin: (userId: string, isTenantAdmin: boolean) =>
    request<User>(`/users/${userId}/tenant-admin`, { method: 'PATCH', body: { isTenantAdmin } }),
  setBillingAdmin: (userId: string, isBillingAdmin: boolean) =>
    request<User>(`/users/${userId}/billing-admin`, {
      method: 'PATCH',
      body: { isBillingAdmin },
      skipTenant: true,
    }),

  // ---- approver assignments (who can approve, by scope) ----
  /** All approver assignments for the active workspace (approverName mapper-populated). */
  listApproverAssignments: (signal?: AbortSignal) =>
    request<ApproverAssignment[]>('/approver-assignments', { signal }),
  /** Upsert an approver assignment per (approver, scope, scopeValue). TENANT_ADMIN. */
  saveApproverAssignment: (dto: SaveApproverAssignmentDto) =>
    request<ApproverAssignment>('/approver-assignments', { method: 'POST', body: dto }),
  /** Remove an approver assignment (204 on success). TENANT_ADMIN. */
  deleteApproverAssignment: (id: string) =>
    request<void>(`/approver-assignments/${id}`, { method: 'DELETE' }),

  // ---- entra groups (invite teammates from a Microsoft 365 / Entra group) ----
  /** The Entra directory (tenant) id the app authenticates against — shown in App Settings. */
  entraDirectory: (signal?: AbortSignal) =>
    request<EntraDirectoryInfo>('/entra-groups/directory', { signal }),
  /** The Microsoft 365 (Unified) groups in the directory, for the group picker. TENANT_ADMIN. */
  availableEntraGroups: (signal?: AbortSignal) =>
    request<EntraDirectoryGroup[]>('/entra-groups/available', { signal }),
  /** Microsoft 365 / Entra groups registered as an invite source for the active workspace. */
  listEntraGroups: (signal?: AbortSignal) =>
    request<RegisteredEntraGroup[]>('/entra-groups', { signal }),
  /** Register an Entra group (Object ID + display name) as an invite source. TENANT_ADMIN. */
  registerEntraGroup: (dto: RegisterEntraGroupDto) =>
    request<RegisteredEntraGroup>('/entra-groups', { method: 'POST', body: dto }),
  /** Unregister an Entra group (204 on success). TENANT_ADMIN. */
  deleteEntraGroup: (id: string) =>
    request<void>(`/entra-groups/${id}`, { method: 'DELETE' }),
  /** Members of a registered Entra group, resolved live from Microsoft Graph. */
  entraGroupMembers: (id: string, signal?: AbortSignal) =>
    request<EntraGroupMember[]>(`/entra-groups/${id}/members`, { signal }),
  /** Invite selected group members into the active workspace with the given roles. TENANT_ADMIN. */
  inviteFromGroup: (dto: InviteFromGroupDto) =>
    request<{ invited: number }>('/entra-groups/invite', { method: 'POST', body: dto }),

  // ---- sources ----
  listSources: (signal?: AbortSignal) => request<Source[]>('/sources', { signal }),
  getSource: (id: string) => request<Source>(`/sources/${id}`),
  createSource: (dto: CreateSourceDto) =>
    request<Source>('/sources', { method: 'POST', body: dto }),
  updateSource: (id: string, dto: UpdateSourceDto) =>
    request<Source>(`/sources/${id}`, { method: 'PATCH', body: dto }),
  deleteSource: (id: string) => request<void>(`/sources/${id}`, { method: 'DELETE' }),
  pullSource: (id: string) => request<SourcePull>(`/sources/${id}/pull`, { method: 'POST' }),
  listSourcePulls: (id: string, signal?: AbortSignal) =>
    request<SourcePull[]>(`/sources/${id}/pulls`, { signal }),
  listSourceItems: (id: string, signal?: AbortSignal) =>
    request<SourceItem[]>(`/sources/${id}/items`, { signal }),
  /**
   * One page of a source's items, newest first, with an optional search (title, summary, author,
   * link). Pass the previous page's `nextCursor` as `cursor` for the next one.
   */
  listSourceItemsPage: (
    id: string,
    page: { q?: string; cursor?: string | null; limit?: number } = {},
    signal?: AbortSignal,
  ) =>
    request<SourceItemPage>(`/sources/${id}/items/page`, {
      query: {
        q: page.q?.trim() || undefined,
        cursor: page.cursor ?? undefined,
        limit: page.limit,
      },
      signal,
    }),

  // ---- trend topics (per-user keyword/phrase tags that groom the digest) ----
  listTrendTopics: (signal?: AbortSignal) => request<TrendTopic[]>('/trend-topics', { signal }),
  createTrendTopic: (dto: CreateTrendTopicDto) =>
    request<TrendTopic>('/trend-topics', { method: 'POST', body: dto }),
  /** Add several topics at once (comma-separated paste) — returns the full updated list. */
  createTrendTopics: (phrases: string[]) =>
    request<TrendTopic[]>('/trend-topics/bulk', { method: 'POST', body: { phrases } }),
  /** AI-suggested topic phrases grounded in Brand/Company/Personal Voice. */
  getTrendSuggestions: (signal?: AbortSignal) =>
    request<string[]>('/trend-topics/suggestions', { signal }),
  /** "Used by teammates": topics other workspace members track that the user does not. */
  getTeammateTopics: (signal?: AbortSignal) =>
    request<TeammateTopic[]>('/trend-topics/teammates', { signal }),
  deleteTrendTopic: (id: string) => request<void>(`/trend-topics/${id}`, { method: 'DELETE' }),

  // ---- social connections ----
  listSocialConnections: (signal?: AbortSignal) =>
    request<SocialConnection[]>('/social/connections', { signal }),
  connectSocial: (platform: SocialPlatform, dto: ConnectSocialDto) =>
    request<SocialConnection>(`/social/connections/${platform}/connect`, {
      method: 'POST',
      body: dto,
    }),
  disconnectSocial: (connectionId: string) =>
    request<void>(`/social/connections/${connectionId}/disconnect`, { method: 'POST' }),

  // ---- analytics (owned-account performance snapshots) ----
  /**
   * Engagement Overview: an engagements/reach daily series + totals over a trailing range
   * (default 7 days) for ONE connected account (`account`, a connection id) — or, with no
   * account, for every connected account added together. `platform` is the older per-platform
   * filter; nothing in the app sends it any more.
   */
  getAnalyticsOverview: (
    opts?: { account?: string; platform?: SocialPlatform; range?: number },
    signal?: AbortSignal,
  ) =>
    request<AnalyticsOverview>('/analytics/overview', {
      query: { account: opts?.account, platform: opts?.platform, range: opts?.range },
      signal,
    }),
  /** Account Performance: one row per connected account — followers/reach/engagement + trend. */
  getPlatformPerformance: (signal?: AbortSignal) =>
    request<PlatformPerformance[]>('/analytics/platforms', { signal }),
  /**
   * Top Performing Posts for the page's current filters (`account` as for the Overview).
   *
   * `sort` is part of the REQUEST, not a client-side reorder: ranking by shares selects a different
   * set of posts than ranking by likes, so each sort is its own query (and its own cache entry).
   */
  getTopPosts: (
    opts: { account?: string; platform?: SocialPlatform; range: number; sort: TopPostSort },
    signal?: AbortSignal,
  ) =>
    request<TopPosts>('/analytics/top-posts', {
      query: { account: opts.account, platform: opts.platform, range: opts.range, sort: opts.sort },
      signal,
    }),

  // ---- messages (consolidated social inbox) ----
  getInboxConversations: (signal?: AbortSignal) =>
    request<InboxConversation[]>('/messages/conversations', { signal }),
  replyToConversation: (conversationId: string, text: string) =>
    request<void>('/messages/reply', { method: 'POST', body: { conversationId, text } }),
  /** Sync "seen" back to the platform (DMs); no-op server-side for comments/mentions. */
  markConversationSeen: (conversationId: string) =>
    request<void>('/messages/conversations/seen', { method: 'POST', body: { conversationId } }),

  // ---- Canva Connect (install-level OAuth against the admin's own Canva integration) ----
  /**
   * Begin the Canva Connect OAuth round-trip — returns the authorize URL to send the whole
   * browser to (the API keeps the PKCE verifier server-side). 400 when the Client ID + Secret
   * aren't saved yet. Install-wide (skipTenant); Tenant-Admin only.
   */
  canvaConnect: () =>
    request<{ url: string }>('/site-integrations/canva/connect', {
      method: 'POST',
      skipTenant: true,
    }),
  /**
   * Clear the stored Canva OAuth tokens. The Client ID + Secret stay saved so the admin can
   * reconnect without re-entering them. Install-wide (skipTenant); Tenant-Admin only.
   */
  canvaDisconnect: () =>
    request<void>('/site-integrations/canva/disconnect', {
      method: 'POST',
      skipTenant: true,
    }),

  // ---- Facebook OAuth connect flow (the start: startFacebookConnect) ----
  /** List the Facebook Pages the authorized user manages for a pending connection. */
  facebookPages: (connectionId: string, signal?: AbortSignal) =>
    request<FacebookPage[]>('/social/facebook/pages', {
      query: { connectionId },
      signal,
    }),
  /** Select which Facebook Page to connect; returns the updated connection. */
  facebookSelectPage: (connectionId: string, pageId: string) =>
    request<SocialConnection>('/social/facebook/select-page', {
      method: 'POST',
      body: { connectionId, pageId },
    }),

  // ---- Instagram via Facebook Login (the start: startInstagramConnect) ----
  /** List the Instagram Business accounts linked to your Facebook Pages. */
  instagramFbAccounts: (connectionId: string, signal?: AbortSignal) =>
    request<InstagramAccount[]>('/social/instagram-fb/accounts', {
      query: { connectionId },
      signal,
    }),
  /** Select an Instagram account to finish connecting. */
  instagramFbSelectAccount: (connectionId: string, igUserId: string) =>
    request<SocialConnection>('/social/instagram-fb/select-account', {
      method: 'POST',
      body: { connectionId, igUserId },
    }),

  // ---- LinkedIn OAuth connect flow (the start: startLinkedInConnect) ----
  /** List the Company Pages the member administers (Community Management, post-OAuth). */
  linkedinOrganizations: (connectionId: string, signal?: AbortSignal) =>
    request<LinkedInOrganization[]>('/social/linkedin/organizations', {
      query: { connectionId },
      signal,
    }),
  /** Select an organization to finish a Community Management connection. */
  linkedinSelectOrganization: (connectionId: string, organizationId: string) =>
    request<SocialConnection>('/social/linkedin/select-organization', {
      method: 'POST',
      body: { connectionId, organizationId },
    }),

  // ---- OAuth callback URLs (public) ----
  /**
   * The callback URL the server sends to each platform: what the install's developer app
   * registers. Public, with no sign-in and no workspace (the setup guides open in a new tab
   * outside the sign-in), so no Authorization or X-Tenant header is attached and no token is asked
   * for or refreshed.
   */
  getOAuthRedirectUris: (signal?: AbortSignal) =>
    request<OAuthRedirectUris>('/social/redirect-uris', { skipAuth: true, skipTenant: true, signal }),

  // ---- Starting a connection with the install's Site Integrations keys ----
  // The server reads the app keys of the platform's credential set from Site Integrations (409
  // SOCIAL_APP_KEYS_NOT_SET while it has none), so no start sends credentials. Each answers with the
  // provider URL to send the browser to.
  /** Begin connecting a Facebook Page. No body. */
  startFacebookConnect: () =>
    request<FacebookStartResult>('/social/facebook/start', { method: 'POST' }),
  /** Begin connecting an Instagram account through Facebook Login (the Facebook set). No body. */
  startInstagramConnect: () =>
    request<FacebookStartResult>('/social/instagram-fb/start', { method: 'POST' }),
  /** Begin connecting LinkedIn as the chosen connection type, which picks its credential set. */
  startLinkedInConnect: (apiType: LinkedInApiType) =>
    request<LinkedInStartResult>('/social/linkedin/start', {
      method: 'POST',
      body: { apiType } satisfies LinkedInStartDto,
    }),
  /** Begin connecting Threads or X. No body. (A NEW Instagram Login connection can no longer start.) */
  startOAuthConnect: (platform: SocialPlatform.THREADS | SocialPlatform.X) =>
    request<OAuthStartResult>(`/social/oauth/${platform.toLowerCase()}/start`, { method: 'POST' }),

  // ---- briefs ----
  getBrief: (date?: string, signal?: AbortSignal) =>
    request<DailyBrief>('/briefs', { query: { date }, signal }),
  listBriefDates: (signal?: AbortSignal) => request<string[]>('/briefs/dates', { signal }),
  generateBrief: (date?: string, maxThemes?: number) =>
    request<DailyBrief>('/briefs/generate', {
      method: 'POST',
      query: { date, maxThemes: maxThemes != null ? String(maxThemes) : undefined },
    }),
  /** The SourceItems that fed a theme ("View sources"). */
  briefThemeSources: (briefId: string, themeId: string, signal?: AbortSignal) =>
    request<SourceItem[]>(`/briefs/${briefId}/themes/${themeId}/sources`, { signal }),
  themeToStrategist: (briefId: string, themeId: string) =>
    request<Campaign>(`/briefs/${briefId}/themes/${themeId}/to-strategist`, {
      method: 'POST',
    }),
  /**
   * "Write Post with Strategist" — an empty chat titled after the theme, plus the prompt to send
   * into it. The prompt comes back UNSENT so this returns in one round trip instead of a model
   * turn; the chat sends it, which is what shows it while the strategist is thinking.
   */
  themeToStrategistChat: (briefId: string, themeId: string) =>
    request<SeededStrategistChat>(`/briefs/${briefId}/themes/${themeId}/discuss`, {
      method: 'POST',
    }),

  // ---- campaigns (top-level grouping; owns the approval chain + release) ----
  /** All campaigns for the tenant (list view — carries `postCount`, no posts). */
  listCampaigns: (signal?: AbortSignal) => request<Campaign[]>('/campaigns', { signal }),
  /** One campaign with its `posts[]` (each with `contents[]`). */
  getCampaign: (id: string, signal?: AbortSignal) =>
    request<Campaign>(`/campaigns/${id}`, { signal }),
  createCampaign: (dto: CreateCampaignDto) =>
    request<Campaign>('/campaigns', { method: 'POST', body: dto }),
  updateCampaign: (id: string, dto: UpdateCampaignDto) =>
    request<Campaign>(`/campaigns/${id}`, { method: 'PATCH', body: dto }),
  deleteCampaign: (id: string) => request<void>(`/campaigns/${id}`, { method: 'DELETE' }),
  /** Move DRAFT/REVISE → IN_REVIEW, optionally notifying named stakeholders. */
  submitCampaign: (id: string, dto: SubmitForApprovalDto) =>
    request<Campaign>(`/campaigns/${id}/submit-for-approval`, { method: 'POST', body: dto }),
  /** Approve / request-changes / reject a campaign under review. */
  decideCampaign: (id: string, dto: ApprovalDecisionDto) =>
    request<Campaign>(`/campaigns/${id}/decision`, { method: 'POST', body: dto }),
  /** Move IN_REVIEW → COMPLIANCE_CHECK (healthcare packs). */
  complianceCheckCampaign: (id: string) =>
    request<Campaign>(`/campaigns/${id}/compliance-check`, { method: 'POST' }),
  /** Withdraw a campaign from review back to DRAFT (author or workspace admin). */
  cancelReviewCampaign: (id: string) =>
    request<Campaign>(`/campaigns/${id}/cancel-review`, { method: 'POST' }),
  /** Append-only approval transition history for a campaign. */
  getCampaignTransitions: (id: string, signal?: AbortSignal) =>
    request<ApprovalTransition[]>(`/campaigns/${id}/transitions`, { signal }),

  // ---- posts (one destination; owns Content; release gated by its campaign) ----
  /** Flat, cross-campaign list (carries `campaignName` + `campaignApprovalState`, no contents). */
  listPosts: (signal?: AbortSignal) => request<Post[]>('/posts', { signal }),
  /** One post with its `contents[]`. */
  getPost: (id: string, signal?: AbortSignal) => request<Post>(`/posts/${id}`, { signal }),

  /** A set of sibling destinations, with just enough per-destination detail to label a tab. */
  getPostGroup: (id: string, signal?: AbortSignal) =>
    request<PostGroup>(`/post-groups/${id}`, { signal }),

  /**
   * Add a destination by copying this post's content to another connected account. Forms the set on
   * demand, so the caller does not need a group to exist first.
   */
  seedDestination: (fromPostId: string, socialConnectionId: string) =>
    request<PostGroup>('/post-groups/destinations', {
      method: 'POST',
      body: { fromPostId, socialConnectionId },
    }),

  /**
   * Overwrite other destinations with this one's current content, once. Returns a row per requested
   * destination — refusals included, since a partial result is the normal outcome.
   */
  copyToDestinations: (fromPostId: string, toPostIds: string[]) =>
    request<CopyDestinationResult[]>('/post-groups/copy', {
      method: 'POST',
      body: { fromPostId, toPostIds },
    }),

  /**
   * "Apply Same Media to Set?" — the panel's media onto every other destination in the set, trimmed
   * to what each platform accepts. A row per sibling, refusals included.
   */
  applyMediaToSet: (fromPostId: string, assetIds: string[]) =>
    request<ApplyMediaToSetResult>('/post-groups/apply-media', {
      method: 'POST',
      body: { fromPostId, assetIds },
    }),

  /** Detach a destination. The post survives, standalone — nothing is deleted. */
  detachDestination: (groupId: string, postId: string) =>
    request<PostGroup>(`/post-groups/${groupId}/destinations/${postId}`, { method: 'DELETE' }),
  createPost: (dto: CreatePostDto) => request<Post>('/posts', { method: 'POST', body: dto }),
  updatePost: (id: string, dto: UpdatePostDto) =>
    request<Post>(`/posts/${id}`, { method: 'PATCH', body: dto }),
  deletePost: (id: string) => request<void>(`/posts/${id}`, { method: 'DELETE' }),
  /**
   * Duplicate a post (any status) into `campaignId` — or the one-off bucket when omitted.
   * The copy comes back as a fresh DRAFT with the body copied and all release state reset.
   */
  duplicatePost: (id: string, dto: DuplicatePostDto = {}) =>
    request<Post>(`/posts/${id}/duplicate`, { method: 'POST', body: dto }),
  /** Move an editable post to another campaign (or the one-off bucket when omitted). */
  movePost: (id: string, dto: MovePostDto = {}) =>
    request<Post>(`/posts/${id}/move`, { method: 'POST', body: dto }),
  /** Append a Content piece (TEXT/IMAGE/AUDIO/VIDEO) to a post. */
  addContent: (postId: string, dto: CreateContentDto) =>
    request<Content>(`/posts/${postId}/contents`, { method: 'POST', body: dto }),
  /**
   * Replace ALL of a post's content in one write — how a thread is saved, and how a saved thread is
   * cut back to one post. The array order is the order. Returns the post, whose `contents[i]` is
   * the row written for `dto.contents[i]`.
   */
  replaceContents: (postId: string, dto: ReplaceContentsDto) =>
    request<Post>(`/posts/${postId}/contents`, { method: 'PUT', body: dto }),
  /**
   * AI-generate text for a post. Returns `{ title?, body, citations }` — NOT
   * persisted; the caller applies the body to a TEXT content piece.
   */
  generatePost: (id: string, dto: GenerateContentDto) =>
    request<GeneratedContent>(`/posts/${id}/generate`, { method: 'POST', body: dto }),
  /**
   * "Convert to thread with AI": split one over-limit X or Threads post's text into the posts of a
   * thread. Raw text in, posts out - no post is read or saved, so a new post needs no draft first.
   * `signal` cancels it; an aborted request rejects like any network error, so the caller tells a
   * Cancel from a failure by its own state, never by the error.
   */
  convertToThread: (dto: ConvertToThreadDto, signal?: AbortSignal) =>
    request<ThreadConversion>('/posts/convert-to-thread', { method: 'POST', body: dto, signal }),
  /** Submit a post for per-post approval (POST mode only): DRAFT/REVISE → IN_REVIEW. */
  submitPostForApproval: (id: string, dto: SubmitForApprovalDto) =>
    request<Post>(`/posts/${id}/submit-for-approval`, { method: 'POST', body: dto }),
  /** Approve / request-changes / reject a post under review (POST mode only). */
  decidePost: (id: string, dto: ApprovalDecisionDto) =>
    request<Post>(`/posts/${id}/decision`, { method: 'POST', body: dto }),
  /** POST mode: clear a post's compliance stage (COMPLIANCE_CHECK → APPROVED). */
  compliancePostCheck: (id: string) =>
    request<Post>(`/posts/${id}/compliance-check`, { method: 'POST' }),
  /** POST mode: withdraw a post from review back to DRAFT (campaign author or workspace admin). */
  // ---- Lane approvals (CAMPAIGN mode: Blog / Social / Video approve separately) ----
  submitCampaignLane: (id: string, channel: ContentChannel, dto: SubmitForApprovalDto) =>
    request<Campaign>(`/campaigns/${id}/lanes/${channel}/submit-for-approval`, {
      method: 'POST',
      body: dto,
    }),
  decideCampaignLane: (id: string, channel: ContentChannel, dto: ApprovalDecisionDto) =>
    request<Campaign>(`/campaigns/${id}/lanes/${channel}/decision`, { method: 'POST', body: dto }),
  cancelCampaignLaneReview: (id: string, channel: ContentChannel) =>
    request<Campaign>(`/campaigns/${id}/lanes/${channel}/cancel-review`, { method: 'POST' }),
  /** Take an approved or scheduled lane back to draft for editing. Clears its schedule. */
  reopenCampaignLane: (id: string, channel: ContentChannel) =>
    request<Campaign>(`/campaigns/${id}/lanes/${channel}/reopen`, { method: 'POST' }),
  complianceApproveCampaignLane: (id: string, channel: ContentChannel) =>
    request<Campaign>(`/campaigns/${id}/lanes/${channel}/compliance-check`, { method: 'POST' }),

  cancelReviewPost: (id: string) =>
    request<Post>(`/posts/${id}/cancel-review`, { method: 'POST' }),

  /**
   * Reopen an approved or scheduled post for editing. Clears its schedule — see the service; a post
   * left holding a past time would file itself under Failed while it was being rewritten.
   */
  reopenPost: (id: string) => request<Post>(`/posts/${id}/reopen`, { method: 'POST' }),
  /** Choose the connected account a post publishes to (allowed on an approved, scheduled or failed post too). */
  setPostAccount: (id: string, socialConnectionId: string) =>
    request<Post>(`/posts/${id}/account`, { method: 'PATCH', body: { socialConnectionId } }),

  /**
   * Record that an export-destination post went live, or is scheduled to on the client's platform.
   * The manual stand-in for a send this destination has no integration for.
   */
  markPostPublished: (id: string, dto: MarkPublishedDto) =>
    request<Post>(`/posts/${id}/mark-published`, { method: 'POST', body: dto }),
  /** Append-only approval history for a single post (per-post history in the composer). */
  getPostTransitions: (id: string, signal?: AbortSignal) =>
    request<ApprovalTransition[]>(`/posts/${id}/transitions`, { signal }),
  schedulePost: (id: string, scheduledAt: string) =>
    request<Post>(`/posts/${id}/schedule`, { method: 'POST', body: { scheduledAt } }),
  /** Toggle "post as soon as approved" (no fixed time → publishes on final approval). */
  setPostPublishWhenApproved: (id: string, value: boolean) =>
    request<Post>(`/posts/${id}/publish-when-approved`, { method: 'POST', body: { value } }),
  /** AI-ranked shortlist of open posting slots for a post (composer "Suggested Slots"). */
  getScheduleSuggestions: (id: string, signal?: AbortSignal) =>
    request<ScheduleSuggestionsResponse>(`/posts/${id}/schedule-suggestions`, { signal }),
  /** Place a post via the posting queue (next / prioritize / month). */
  enqueuePost: (id: string, dto: EnqueuePostDto) =>
    request<Post>(`/posts/${id}/enqueue`, { method: 'POST', body: dto }),

  /**
   * Give every other destination in this post's set the same scheduling decision.
   *
   * The decision travels, not the resolved time: each sibling re-runs the placement against its own
   * platform's queue, because "next available" means something different on each timeline.
   */
  applyScheduleToSet: (postId: string, dto: ApplyScheduleToSetDto) =>
    request<ApplyScheduleToSetResult>(`/posts/${postId}/schedule-set`, {
      method: 'POST',
      body: dto,
    }),
  /**
   * The workspace's posting schedules: the timezone, General's times (`slots`) and each category's
   * (`categories`). Seeds General's default times on the first read. Read `categories ?? []`: an
   * older API (mid-rollout) does not send them.
   */
  getPostingSchedule: (signal?: AbortSignal) =>
    request<PostingSchedule>(`/posting-schedule`, { signal }),
  /**
   * Save every schedule's times in one request (workspace admins): General always, and each category
   * listed in `categories`. A category not listed keeps its times.
   */
  updatePostingSchedule: (dto: UpdatePostingScheduleDto) =>
    request<PostingSchedule>(`/posting-schedule`, { method: 'PUT', body: dto }),

  // ---- scheduling categories (a posting schedule each; Scheduler Settings + the composer field) ----
  /** Every category of the workspace, in creation order. Never seeds anything (any member). */
  listSchedulingCategories: (signal?: AbortSignal) =>
    request<SchedulingCategory[]>('/scheduling-categories', { signal }),
  /** Create a category with no posting times yet (workspace admins). 409 when the name is taken. */
  createSchedulingCategory: (dto: CreateSchedulingCategoryDto) =>
    request<SchedulingCategory>('/scheduling-categories', { method: 'POST', body: dto }),
  /** Rename and/or recolour a category (workspace admins). 409 when the new name is taken. */
  updateSchedulingCategory: (id: string, dto: UpdateSchedulingCategoryDto) =>
    request<SchedulingCategory>(`/scheduling-categories/${id}`, { method: 'PATCH', body: dto }),
  /** How many posts (not in the Trash) name the category, for the delete confirmation (workspace admins). */
  getSchedulingCategoryUsage: (id: string, signal?: AbortSignal) =>
    request<SchedulingCategoryUsage>(`/scheduling-categories/${id}/usage`, { signal }),
  /** Delete a category (workspace admins): its times go with it, its posts move to General. */
  deleteSchedulingCategory: (id: string) =>
    request<void>(`/scheduling-categories/${id}`, { method: 'DELETE' }),
  /**
   * Publish a post now. SOCIAL posts are delivered LIVE to the connected account
   * (verified; marked failed with a reason on error) — no n8n. Non-social posts
   * (Blog/Video/Podcast) hand off to the n8n `content.published` automation.
   */
  publishPost: (id: string) => request<Post>(`/posts/${id}/publish`, { method: 'POST' }),
  /** Natively publish a social post straight to one connected account (no n8n). */
  publishPostNow: (id: string, connectionId: string) =>
    request<Post>(`/posts/${id}/publish-now`, { method: 'POST', body: { connectionId } }),
  /**
   * Say what happened to the one post of a thread whose send was started and never confirmed: it is
   * already live (`adopt`, with the link to it) or it didn't go out (`resend`).
   */
  resolveThreadPost: (postId: string, dto: ResolveThreadPostDto) =>
    request<Post>(`/posts/${postId}/thread/resolve`, { method: 'POST', body: dto }),

  // ---- contents (single media/text elements of a post) ----
  updateContent: (id: string, dto: UpdateContentDto) =>
    request<Content>(`/contents/${id}`, { method: 'PATCH', body: dto }),
  deleteContent: (id: string) => request<void>(`/contents/${id}`, { method: 'DELETE' }),

  // ---- calendar ----
  getCalendar: (from: string, to: string, filter?: CalendarFilter, signal?: AbortSignal) =>
    request<CalendarItem[]>('/calendar', { query: { from, to, filter }, signal }),

  // ---- AI Strategist (per-user chat) ----
  listConversations: (signal?: AbortSignal) =>
    request<StrategistConversation[]>('/strategist/conversations', { signal }),
  createConversation: () =>
    request<StrategistConversation>('/strategist/conversations', { method: 'POST' }),
  getConversation: (id: string, signal?: AbortSignal) =>
    request<StrategistConversationWithMessages>(`/strategist/conversations/${id}`, { signal }),
  sendStrategistMessage: (id: string, content: string) =>
    request<SendMessageResult>(`/strategist/conversations/${id}/messages`, {
      method: 'POST',
      body: { content },
    }),
  deleteConversation: (id: string) =>
    request<void>(`/strategist/conversations/${id}`, { method: 'DELETE' }),
  /** Thumbs up / down on one assistant reply; `null` clears it. Returns the message as stored. */
  setStrategistMessageFeedback: (
    conversationId: string,
    messageId: string,
    rating: ChatFeedback | null,
  ) =>
    request<ChatMessage>(
      `/strategist/conversations/${conversationId}/messages/${messageId}/feedback`,
      { method: 'PUT', body: { rating } },
    ),

  // ---- brand voice & assets ----
  getBrandVoice: (signal?: AbortSignal) => request<BrandVoiceProfile>('/brand-voice', { signal }),
  updateBrandVoice: (dto: UpdateBrandVoiceDto) =>
    request<BrandVoiceProfile>('/brand-voice', { method: 'PUT', body: dto }),

  /**
   * Upload an image (multipart, field `file`) into the tenant asset library and
   * return the created asset. Its `blobUrl` is the auth-proxied image URL
   * (`/api/assets/:id/image`) suitable for storing in `brandAssets.logos`.
   */
  uploadAsset: async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    form.append('generator', AssetGenerator.UPLOAD);
    await appendVideoPoster(form, file);
    return requestMultipart<Asset>('/assets/upload', form);
  },

  // ---- Media generation (held outside the library until saved) ----
  /** Start a generation, or a refinement when parentId + changeRequest are set. */
  startGeneration: (dto: StartGenerationDto) =>
    request<MediaGenerationView>('/media/generate', { method: 'POST', body: dto }),

  /** Poll one generation — the library grid no longer watches these. */
  getGeneration: (id: string, signal?: AbortSignal) =>
    request<MediaGenerationView>(`/media/generations/${id}`, { signal }),

  /** Spend against this workspace's rolling daily generation cap. */
  getGenerationUsage: (signal?: AbortSignal) =>
    request<MediaGenerationUsage>('/media/generation-usage', { signal }),

  /** The generated bytes (auth-gated), as a Blob for an object URL. */
  fetchGenerationImage: (id: string, signal?: AbortSignal) =>
    requestBlob(`/media/generations/${id}/image`, signal),

  /** Promote a generation into the Media Library — the only action that creates an Asset. */
  saveGeneration: (id: string, dto: SaveGenerationDto) =>
    request<Asset>(`/media/generations/${id}/save`, { method: 'POST', body: dto }),

  // ---- Design tools (import finished artwork FROM Canva/Figma) ----
  /** Which design tools this install has wired up. Readable by any member, unlike site-integrations. */
  getDesignTools: (signal?: AbortSignal) =>
    request<DesignToolsView>('/platform-settings/design-tools', { signal }),

  /** Search the connected Canva account's designs. Empty query = most recently modified. */
  searchCanvaDesigns: (query: string, signal?: AbortSignal) =>
    request<DesignToolItem[]>('/design-tools/canva/designs', { query: { query: query || undefined }, signal }),

  /** Resolve a Figma file key to its name + top-level frames. */
  getFigmaFile: (fileKey: string, signal?: AbortSignal) =>
    request<FigmaFileView>('/design-tools/figma/file', { query: { fileKey }, signal }),

  /** Export a Canva design and store it in the Media Library. */
  importCanvaDesign: (dto: CanvaImportDto & { title?: string }) =>
    request<Asset>('/design-tools/canva/import', { method: 'POST', body: dto }),

  /** Render a Figma frame and store it in the Media Library. */
  importFigmaFrame: (dto: FigmaImportDto & { title?: string }) =>
    request<Asset>('/design-tools/figma/import', { method: 'POST', body: dto }),

  // ---- Design bundle (hand a post to Canva/Figma as editable starting material) ----
  /** What a bundle for this post would contain, so the UI can describe it before downloading. */
  getDesignBundlePreview: (postId: string, signal?: AbortSignal) =>
    request<DesignBundlePreview>(`/design-bundle/post/${postId}/preview`, { signal }),

  /**
   * Build and download the post's design bundle. Resolves only once the whole ZIP has arrived —
   * artboards plus full-size images take a moment, so callers keep a pending state up.
   */
  downloadDesignBundle: (postId: string, target: DesignBundleTarget) =>
    requestZip(`/design-bundle/post/${postId}`, { target }),

  /**
   * Download a post as a file (Blog "Export" destination). GET, because it takes no body and the
   * server builds it from SAVED content — the same constraint the design bundle carries.
   */
  exportPost: async (
    postId: string,
    format: BlogExportFormat,
  ): Promise<{ blob: Blob; filename: string | null }> => {
    const res = await authFetch(buildUrl(`/posts/${postId}/export?format=${format}`), {
      method: 'GET',
    });
    if (!res.ok) {
      throw new ApiRequestError(res.status, 'Could not export this post.');
    }
    const disposition = res.headers.get('Content-Disposition') ?? '';
    const match = /filename="?([^"]+)"?/.exec(disposition);
    return { blob: await res.blob(), filename: match?.[1] ?? null };
  },

  /** Fetch an asset image by its proxy `blobUrl` as a Blob (auth-protected). null on 404. */
  fetchAssetBlob: (proxyUrl: string, signal?: AbortSignal, tenantSlug?: string) =>
    requestBlobUrl(proxyUrl, signal, tenantSlug),

  // ---- Generate Design ----
  /**
   * Generate one design and get back the Media Library asset it was saved as.
   *
   * There is no separate save call: the server writes the asset as part of generating, because a
   * result the user has to remember to keep is a result they lose.
   */
  generateDesign: (body: GenerateDesignRequest) =>
    request<GenerateDesignResponse>('/design-generation', { method: 'POST', body }),

  /**
   * Change one thing about a finished design.
   *
   * Returns the same two shapes as generating, because a refinement that leaves the image alone is
   * fast and one that regenerates art is not.
   */
  refineDesign: (body: { assetId: string; request: string }) =>
    request<GenerateDesignResponse>('/design-generation/refine', { method: 'POST', body }),

  /**
   * Where a design job has got to.
   *
   * Only reached when Generate returned `pending`, which happens exactly when imagery is being
   * generated — the one slow part of making a design.
   */
  designJobState: (jobId: string, signal?: AbortSignal) =>
    request<DesignJobState>(`/design-generation/jobs/${encodeURIComponent(jobId)}`, { signal }),

  /**
   * A finished design as a PNG, saved to the Media Library, for a post to carry.
   *
   * The same copy comes back for the same design, so pressing Save twice adds one item, not two.
   */
  designPngCopy: (assetId: string) =>
    request<DesignPngCopy>(`/design-generation/${encodeURIComponent(assetId)}/png`, {
      method: 'POST',
    }),

  /** "Generate image prompt from your post": the post's text in, a prompt for the image model out. */
  promptFromPost: (body: PromptFromPostRequest, signal?: AbortSignal) =>
    request<PromptFromPostResponse>('/media/prompt-from-post', { method: 'POST', body, signal }),

  /**
   * Font families the design server can measure line breaks for.
   *
   * Advisory, not a whitelist: the Brand Workspace typography fields accept any family, because the
   * design names the brand's font regardless of whether this server has it.
   */
  listDesignFonts: (signal?: AbortSignal) =>
    request<{ families: string[] }>('/design-generation/fonts', { signal }),

  /**
   * Render the design Generate WOULD produce, without producing it.
   *
   * Free and instant — no model call, no image call, no write. The modal calls this whenever the
   * template, size or colour treatment changes, which is what keeps the thumbnail honest about the
   * size actually selected.
   */
  previewDesign: (
    body: {
      archetype: string;
      width: number;
      height: number;
      colorEmphasis?: ColorEmphasis;
      colorMode?: ColorMode;
      text?: string;
      subtext?: string;
      hasImage?: boolean;
      variantSeed?: number;
    },
    signal?: AbortSignal,
  ) => request<{ svg: string; notes: string[] }>('/design-generation/preview', { method: 'POST', body, signal }),

  // ---- Brand Workspace design templates (Figma .svg / Canva .pptx) ----
  /** Every template for the active workspace, newest first. Optionally one kind. */
  listBrandTemplates: (kind?: BrandTemplateKind, signal?: AbortSignal) =>
    request<BrandWorkspaceTemplate[]>('/brand-templates', { query: { kind }, signal }),

  /** One template WITH its mappable regions — the list omits those, this is what the mapper reads. */
  getBrandTemplate: (id: string, signal?: AbortSignal) =>
    request<BrandWorkspaceTemplate>(`/brand-templates/${id}`, { signal }),

  /**
   * Upload a template, with an optional preview image.
   *
   * The FILE is what matters; `preview` only ever fills the card for a `.pptx`, which nothing on the
   * server can rasterise. An `.svg` renders itself, so passing one for a Figma template is pointless
   * rather than wrong.
   */
  uploadBrandTemplate: (input: {
    file: File;
    kind: BrandTemplateKind;
    nickname?: string;
    preview?: Blob | null;
  }) => {
    const form = new FormData();
    form.append('file', input.file);
    form.append('kind', input.kind);
    if (input.nickname?.trim()) form.append('nickname', input.nickname.trim());
    if (input.preview) form.append('preview', input.preview, 'preview.png');
    return requestMultipart<BrandWorkspaceTemplate>('/brand-templates', form);
  },

  /** Rename, or save the role mapping. Pass `roleMap: null` to clear it. */
  updateBrandTemplate: (
    id: string,
    changes: { nickname?: string; roleMap?: TemplateRoleMap | null },
  ) =>
    request<BrandWorkspaceTemplate>(`/brand-templates/${id}`, {
      method: 'PATCH',
      body: changes,
    }),

  /** Hard delete — a template is only ever an input, so nothing downstream is stranded. */
  deleteBrandTemplate: (id: string) =>
    request<void>(`/brand-templates/${id}`, { method: 'DELETE' }),

  /**
   * The template outlined and numbered, as SVG MARKUP plus the matching region list.
   *
   * Markup rather than an image because this runtime has no rasteriser; the browser draws it. Both
   * halves come from one response so they cannot describe different parses of the file.
   */
  getBrandTemplateAnnotated: (id: string, signal?: AbortSignal) =>
    request<{ svg: string; regions: TemplateRegion[] }>(`/brand-templates/${id}/annotated`, {
      signal,
    }),

  // ---- Media Library (assets + folders + trash) ----
  /**
   * List Media Library assets with the given filters. `deleted` defaults to false
   * (excludes trash); `deleted: true` returns ONLY the trash. `folderId: 'none'`
   * returns the All-root's unfiled assets; omit it for every folder. `source`/`type`
   * of `'all'` (or omitted) apply no filter.
   */
  listMedia: (query: MediaQuery = {}, signal?: AbortSignal) =>
    request<Asset[]>('/assets', {
      query: {
        source: query.source && query.source !== 'all' ? query.source : undefined,
        type: query.type && query.type !== 'all' ? query.type : undefined,
        folderId: query.folderId,
        campaignId: query.campaignId,
        deleted: query.deleted ? true : undefined,
      },
      signal,
    }),

  /**
   * One page of the Media Library, newest first: the same filters as {@link listMedia} plus a
   * search (`query.q`: name, prompt of a generated file, whole tags). Pass the previous page's
   * `nextCursor` as `cursor` for the next one.
   */
  listMediaPage: (
    query: MediaQuery,
    page: { cursor?: string | null; limit?: number } = {},
    signal?: AbortSignal,
  ) =>
    request<AssetPage>('/assets/page', {
      query: {
        source: query.source && query.source !== 'all' ? query.source : undefined,
        type: query.type && query.type !== 'all' ? query.type : undefined,
        folderId: query.folderId,
        campaignId: query.campaignId,
        deleted: query.deleted ? true : undefined,
        q: query.q?.trim() || undefined,
        cursor: page.cursor ?? undefined,
        limit: page.limit,
      },
      signal,
    }),

  /**
   * One asset by id, trashed ones included - for a link that opens an asset the loaded page of
   * the library does not hold.
   */
  getAsset: (id: string, signal?: AbortSignal) => request<Asset>(`/assets/${id}`, { signal }),

  /**
   * Upload media (image/video/audio, multipart field `file`, ≤100MB) into the tenant
   * Media Library, optionally into a folder. Returns the created asset (its `blobUrl`
   * is the auth-proxied stream URL `/api/assets/:id/image`).
   */
  uploadMedia: async (file: File, opts?: { folderId?: string }) => {
    const form = new FormData();
    form.append('file', file);
    form.append('generator', AssetGenerator.UPLOAD);
    if (opts?.folderId) form.append('folderId', opts.folderId);
    await appendVideoPoster(form, file);
    return requestMultipart<Asset>('/assets/upload', form);
  },

  /**
   * Upload media reporting progress, and cancellable — what the upload modal's per-file rows need.
   *
   * Same endpoint and same poster capture as {@link uploadMedia}; only the transport differs.
   */
  uploadMediaWithProgress: async (
    file: File,
    opts: { folderId?: string; onProgress?: (fraction: number) => void; signal?: AbortSignal },
  ) => {
    const form = new FormData();
    form.append('file', file);
    form.append('generator', AssetGenerator.UPLOAD);
    if (opts.folderId) form.append('folderId', opts.folderId);
    await appendVideoPoster(form, file);
    return requestMultipartWithProgress<Asset>('/assets/upload', form, opts);
  },

  /**
   * Fill in a still frame for a video that has none. Fire-and-forget: the caller has a poster it
   * captured from a video it was playing anyway, and nothing about the page depends on this landing.
   */
  setAssetPoster: async (assetId: string, poster: Blob): Promise<void> => {
    const form = new FormData();
    form.append('poster', poster, 'poster.jpg');
    await authFetch(buildUrl(`/assets/${assetId}/poster`), { method: 'POST', body: form });
  },

  /**
   * AI-generate a media asset from a prompt (lands in `folderId`, or All root if
   * omitted). Sends the full DTO — `provider` ('azure' → first-party FLUX, default;
   * or a BYO `MediaProvider`), plus optional `model`/`size`. A sync provider (FLUX)
   * returns a `status:'ready'` asset; an async one (Magnific) returns a
   * `status:'generating'` placeholder finalized later.
   */
  generateMedia: (dto: GenerateAssetDto) =>
    request<Asset>('/assets/generate', { method: 'POST', body: dto }),

  // ---- Media generator credentials (BYO provider API keys, masked) ----
  /** List the tenant's media-generator credentials (personal + workspace, masked). */
  listMediaCredentials: (signal?: AbortSignal) =>
    request<MediaCredential[]>('/media-credentials', { signal }),

  /** Upsert the install-level media-generator credential for a provider. Returns the masked row. */
  saveMediaCredential: (dto: SaveMediaCredentialDto) =>
    request<MediaCredential>('/media-credentials', { method: 'POST', body: dto }),

  /** Remove a stored media-generator credential (204 on success). */
  deleteMediaCredential: (id: string) =>
    request<void>(`/media-credentials/${id}`, { method: 'DELETE' }),

  /** Move (folderId), rename (name), or retag (tags) an asset. `folderId: null` = All root. */
  updateAsset: (id: string, dto: UpdateAssetDto) =>
    request<Asset>(`/assets/${id}`, { method: 'PATCH', body: dto }),

  /** Soft-delete an asset (moves it to the recoverable trash). */
  deleteAsset: (id: string) => request<void>(`/assets/${id}`, { method: 'DELETE' }),

  /** Restore a soft-deleted asset out of the trash. */
  restoreAsset: (id: string) => request<Asset>(`/assets/${id}/restore`, { method: 'POST' }),

  // ---- Media Library bulk actions (grid multi-select) ----
  /** Move every selected asset into one folder. `folderId: null` = the All root. */
  bulkMoveAssets: (ids: string[], folderId: string | null) =>
    request<BulkAssetResult>('/assets/bulk/move', { method: 'POST', body: { ids, folderId } }),

  /** Soft-delete every selected asset (moves them to the recoverable trash). */
  bulkDeleteAssets: (ids: string[]) =>
    request<BulkAssetResult>('/assets/bulk/delete', { method: 'POST', body: { ids } }),

  /**
   * Download the selected assets as one ZIP. The API streams the archive, so this resolves
   * only once the whole bundle has arrived — callers should show a pending state.
   */
  downloadAssetsZip: (ids: string[]) =>
    requestZip('/assets/bulk/download', { ids }),

  // ---- Media Library folders ----
  /** Manual Media Library folders, each carrying its non-deleted `assetCount`. */
  listAssetFolders: (signal?: AbortSignal) =>
    request<AssetFolder[]>('/asset-folders', { signal }),
  createAssetFolder: (dto: CreateAssetFolderDto) =>
    request<AssetFolder>('/asset-folders', { method: 'POST', body: dto }),
  updateAssetFolder: (id: string, dto: UpdateAssetFolderDto) =>
    request<AssetFolder>(`/asset-folders/${id}`, { method: 'PATCH', body: dto }),
  /** Delete a folder; its assets become unfiled (fall back to the All root). */
  deleteAssetFolder: (id: string) =>
    request<void>(`/asset-folders/${id}`, { method: 'DELETE' }),
};
